import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../components/elements/form_input';
import PhoneInput from '../../components/elements/phone_input';
import Button from '../../components/elements/button';
import FileUpload from '../../components/elements/file_upload';
import EmailFirstAuth from '../../components/auth/EmailFirstAuth';
import { Store, Calendar, CircleCheck } from '../../assets/icons';
import '../../assets/css/vendor/register.css';
import { vendorsApi } from '../../network/apis/vendors';
import { setUser } from '@sentry/react';
import { saveDraft, loadDraft, clearDraft, getDraftAge } from '../../utils/registrationDraft';

const VendorRegister = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1); // Step 1 is now auth
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [draftAge, setDraftAge] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    
    const [formData, setFormData] = useState({
        storeName: '',
        storeDescription: '',
        storeType: '',
        // storeLogo: null,
        // storeLogo_name: '',
        storeEmail: '',
        storePhone: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        businessRegistrationNumber: '',
        taxId: '',
        bankName: '',
        accountNumber: '',
        accountName: '',
        establishedDate: '',
        productCategories: [],
        businessCertificate: null,
        businessCertificate_name: '',
        taxCertificate: null,
        taxCertificate_name: '',
        identityDocument: null,
        identityDocument_name: ''
    });

    const totalSteps = 5; // Auth, Store Info, Contact, Business, Documents

    // Check if already authenticated on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        if (token && user) {
            const userData = JSON.parse(user);
            // If already a vendor, redirect
            if (userData.is_vendor) {
                navigate('/vendor');
                return;
            }
            setIsAuthenticated(true);
            setCurrentUser(userData);
            setCurrentStep(2); // Skip to store info
        }
    }, [navigate]);
    
    // Check for existing draft on mount
    useEffect(() => {
        const draft = loadDraft('vendor');
        if (draft && draft.data.storeName) {
            setDraftAge(getDraftAge('vendor'));
            setShowDraftModal(true);
        }
    }, []);
    
    // Auto-save when form data changes (only for non-auth steps)
    useEffect(() => {
        if (currentStep > 1 && formData.storeName) {
            saveDraft('vendor', formData, currentStep);
        }
    }, [formData, currentStep]);
    
    const handleRestoreDraft = () => {
        const draft = loadDraft('vendor');
        if (draft) {
            setFormData(prev => ({ ...prev, ...draft.data }));
            // Only restore to step 2+ if authenticated
            if (isAuthenticated && draft.currentStep > 1) {
                setCurrentStep(draft.currentStep);
            }
            setSuccess('Draft restored successfully!');
        }
        setShowDraftModal(false);
    };
    
    const handleDiscardDraft = () => {
        clearDraft('vendor');
        setShowDraftModal(false);
    };
    
    // Handle successful authentication from EmailFirstAuth
    const handleAuthSuccess = (user, { isNewUser }) => {
        setIsAuthenticated(true);
        setCurrentUser(user);
        
        // Check if already a vendor
        if (user.is_vendor) {
            setSuccess('You are already registered as a vendor! Redirecting...');
            setTimeout(() => navigate('/vendor'), 1500);
            return;
        }
        
        setSuccess(isNewUser ? 'Account created! Now let\'s set up your store.' : 'Welcome back! Let\'s set up your store.');
        
        // Move to next step after a brief moment
        setTimeout(() => {
            setCurrentStep(2);
            setError(null);
            setSuccess(null);
        }, 1000);
    };

    const handleFileUpload = (field, file) => {
        if (file === null) {
            setFormData({
                ...formData,
                [field]: null,
                [field + '_name']: ''
            });
            return;
        }
        setFormData({
            ...formData,
            [field]: file,
            [field + '_name']: file.name
        });
    };

    // Submit vendor registration (called on final step)
    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        setError(null);
        setSuccess(null);
        
        // Ensure user is authenticated
        if (!isAuthenticated) {
            setError('Please sign in or create an account first');
            setCurrentStep(1);
            return;
        }
        
        setIsSubmitting(true);
        setSuccess('Submitting your store registration...');
        
        try {
            // Create FormData with backend expected field names
            const vendorFormData = new FormData();
            vendorFormData.append('store_name', formData.storeName);
            vendorFormData.append('store_description', formData.storeDescription);
            vendorFormData.append('store_type', formData.storeType);
            vendorFormData.append('business_registration_number', formData.businessRegistrationNumber);
            vendorFormData.append('tax_id', formData.taxId || '');
            vendorFormData.append('bank_name', formData.bankName);
            vendorFormData.append('account_number', formData.accountNumber);
            vendorFormData.append('account_name', formData.accountName);
            vendorFormData.append('address', formData.address);
            vendorFormData.append('city', formData.city);
            vendorFormData.append('state', formData.state);
            vendorFormData.append('postal_code', formData.postalCode || '');
            
            // Append files if they exist
            // if (formData.storeLogo) {
            //     vendorFormData.append('store_logo', formData.storeLogo);
            // }
            if (formData.businessCertificate) {
                vendorFormData.append('business_certificate', formData.businessCertificate);
            }
            if (formData.taxCertificate) {
                vendorFormData.append('tax_certificate', formData.taxCertificate);
            }
            if (formData.identityDocument) {
                vendorFormData.append('identity_document', formData.identityDocument);
            }
            
            await vendorsApi.registerVendor(vendorFormData);
            
            // Clear draft on successful registration
            clearDraft('vendor');
            
            setSuccess('Registration submitted successfully! Your application is under review.');
            if (currentUser) {
                setUser(currentUser); // Set user context for Sentry
            }
            
            setTimeout(() => navigate('/vendor'), 2000);
        } catch (err) {
            // Use the improved error message from interceptor
            let errorMessage = err.displayMessage || 'Registration failed. Please try again.';
            
            // Additional context for file upload errors
            if (err.isFileUploadError) {
                errorMessage = `File upload failed: ${errorMessage}`;
            }
            
            // Handle specific error responses
            if (err.response?.status === 400) {
                const errorData = err.response?.data;
                if (errorData?.errors && typeof errorData.errors === 'object') {
                    const fieldErrors = Object.entries(errorData.errors);
                    if (fieldErrors.length > 0) {
                        const [fieldName, messages] = fieldErrors[0];
                        const message = Array.isArray(messages) ? messages[0] : messages;
                        errorMessage = `${fieldName.replace(/_/g, ' ')}: ${message}`;
                    }
                } else if (errorData?.message) {
                    errorMessage = errorData.message;
                }
            }
            
            setError(errorMessage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStepNavigation = (direction) => {
        setError(null);
        setSuccess(null);
        
        if (direction === 'next') {
            // Step 1 is auth - handled by EmailFirstAuth component
            if (currentStep === 2) {
                // Store Info validation
                if (!formData.storeName.trim()) {
                    setError('Store name is required');
                    return;
                }
                if (!formData.storeType) {
                    setError('Please select store type');
                    return;
                }
            }
            if (currentStep === 3) {
                // Contact validation
                if (!formData.address.trim() || !formData.city.trim() || !formData.state.trim()) {
                    setError('Please fill in all address fields');
                    return;
                }
            }
            if (currentStep === 4) {
                // Business validation
                if (!formData.businessRegistrationNumber.trim()) {
                    setError('Business registration number is required');
                    return;
                }
                if (!formData.bankName.trim() || !formData.accountNumber.trim()) {
                    setError('Bank details are required');
                    return;
                }
            }
            // Step 5 (Documents) validation happens in handleSubmit
        }
        
        // Don't allow going back to step 1 if authenticated
        if (direction === 'previous' && currentStep === 2 && isAuthenticated) {
            return;
        }
        
        setCurrentStep(prev => direction === 'next' ? prev + 1 : prev - 1);
    };

    // Updated step labels - Auth is now step 1
    const stepLabels = ['Account', 'Store Info', 'Contact', 'Business', 'Documents'];
    const stepDescriptions = ['Sign In / Register', 'Basic Details', 'Location Info', 'Bank & Tax', 'Verification'];

    return (
        <div className="vendor-register">
            {/* Draft Restore Modal */}
            {showDraftModal && isAuthenticated && (
                <div className="draft-modal-overlay">
                    <div className="draft-modal">
                        <div className="draft-modal-icon">📝</div>
                        <h3>Continue where you left off?</h3>
                        <p>We found a saved draft from {draftAge}. Would you like to continue with your previous progress?</p>
                        <div className="draft-modal-actions">
                            <Button 
                                text="Start Fresh" 
                                variant="outline" 
                                onClick={handleDiscardDraft}
                            />
                            <Button 
                                text="Restore Draft" 
                                variant="primary" 
                                onClick={handleRestoreDraft}
                            />
                        </div>
                    </div>
                </div>
            )}
            
            <div className="register-container">
                <div className="register-header">
                    <div className="header-icon">
                        <img src={Store} alt="Store" />
                    </div>
                    <h1>Become a Vendor</h1>
                    <p>Register your aquaculture supply store on E-AquaHealth</p>
                </div>

                <div className="progress-tracker">
                    {stepLabels.map((step, index) => (
                        <div 
                            key={index}
                            className={'progress-step ' + (currentStep > index + 1 ? 'completed ' : '') + (currentStep === index + 1 ? 'active' : '')}
                        >
                            <div className="step-indicator">
                                {currentStep > index + 1 ? (
                                    <img src={CircleCheck} className="step-check-icon" alt="completed" />
                                ) : (
                                    index + 1
                                )}
                            </div>
                            <span className="step-label">{step}</span>
                            <span className="step-description">{stepDescriptions[index]}</span>
                        </div>
                    ))}
                </div>

                {/* Error/Success Alert for all steps */}
                {error && currentStep !== 5 && (
                    <div className="registration-form" style={{paddingBottom: 0}}>
                        <div className="alert alert-error">
                            <span className="alert-icon">⚠️</span>
                            <span className="alert-message">{error}</span>
                            <button type="button" className="alert-close" onClick={() => setError(null)}>×</button>
                        </div>
                    </div>
                )}
                {success && currentStep !== 5 && (
                    <div className="registration-form" style={{paddingBottom: 0}}>
                        <div className="alert alert-success">
                            <span className="alert-icon">✓</span>
                            <span className="alert-message">{success}</span>
                            <button type="button" className="alert-close" onClick={() => setSuccess(null)}>×</button>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="registration-form">
                    {/* Step 1: Authentication */}
                    {currentStep === 1 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Welcome</h2>
                                <p className="step-subtitle">Sign in or create an account to get started</p>
                            </div>
                            
                            <EmailFirstAuth 
                                onAuthSuccess={handleAuthSuccess}
                                userType="vendor"
                                disabled={isSubmitting}
                            />
                        </div>
                    )}

                    {/* Step 2: Store Information */}
                    {currentStep === 2 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Store Information</h2>
                                <p className="step-subtitle">Tell us about your store</p>
                            </div>
                            
                            <div className="logo-upload-section">
                                <div className="logo-upload-card">
                                    {/* <div className="upload-preview">
                                        {formData.storeLogo ? (
                                            <img src={URL.createObjectURL(formData.storeLogo)} alt="Store Logo" />
                                        ) : (
                                            <div className="upload-placeholder">
                                                <img src={Store} alt="Store" />
                                                <span>Store Logo</span>
                                            </div>
                                        )}
                                    </div> */}
                                    <div className="upload-actions">
                                        <FileUpload
                                            label="Upload Store Logo"
                                            field="storeLogo"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onUpload={handleFileUpload}
                                            currentFile={formData.storeLogo}
                                            required={false}
                                            hint="Optional. JPG, PNG, WebP (max 15MB)"
                                            disabled={isSubmitting}
                                        />
                                        {formData.storeLogo && (
                                            <Button 
                                                text="Remove" 
                                                variant="outline" 
                                                onClick={() => handleFileUpload('storeLogo', null)}
                                                disabled={isSubmitting}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="form-section">
                                <div className="input-grid">
                                    <FormInput
                                        label="Store Name"
                                        showLabel
                                        icon={<img src={Store} alt="Store" />}
                                        value={formData.storeName}
                                        onChange={(e) => setFormData({...formData, storeName: e.target.value})}
                                        placeholder="e.g., Aqua Supplies Nigeria"
                                    />
                                    
                                    <FormInput
                                        label="Store Type"
                                        showLabel
                                        type="select"
                                        placeholder="Select store type..."
                                        value={formData.storeType}
                                        onChange={(e) => setFormData({...formData, storeType: e.target.value})}
                                    >
                                        <option value="feed_supplier">Feed Supplier</option>
                                        <option value="equipment_supplier">Equipment Supplier</option>
                                        <option value="medicine_supplier">Medicine & Chemicals</option>
                                        <option value="fingerlings">Fingerlings & Juveniles</option>
                                        <option value="pond_construction">Pond Construction</option>
                                        <option value="water_treatment">Water Treatment</option>
                                        <option value="general_store">General Aquaculture Store</option>
                                    </FormInput>

                                    <FormInput
                                        label="Established Date"
                                        showLabel
                                        icon={<img src={Calendar} alt="Calendar" />}
                                        type="date"
                                        value={formData.establishedDate}
                                        onChange={(e) => setFormData({...formData, establishedDate: e.target.value})}
                                    />
                                </div>
                                
                                <FormInput
                                    label="Store Description"
                                    showLabel
                                    type="textarea"
                                    value={formData.storeDescription}
                                    onChange={(e) => setFormData({...formData, storeDescription: e.target.value})}
                                    required
                                    placeholder="Describe your store, products, and services..."
                                    rows={6}
                                    maxLength={1000}
                                />
                            </div>
                            
                            {/* <div className="form-section">
                                <div className="section-title">
                                    <h3>Product Categories</h3>
                                    <span className="section-badge optional">Select all that apply</span>
                                </div>
                                
                                <div className="categories-grid">
                                    {['Fish Feed', 'Shrimp Feed', 'Pond Liners', 'Aerators', 'Water Pumps', 'Nets & Cages', 'Medicines', 'Water Testing Kits', 'Fingerlings', 'Probiotics', 'Pond Equipment', 'Packaging Materials'].map(category => (
                                        <label key={category} className="category-card">
                                            <input
                                                type="checkbox"
                                                checked={formData.productCategories.includes(category)}
                                                onChange={(e) => {
                                                    const cats = e.target.checked
                                                        ? [...formData.productCategories, category]
                                                        : formData.productCategories.filter(c => c !== category);
                                                    setFormData({...formData, productCategories: cats});
                                                }}
                                            />
                                            <span className="category-checkmark"></span>
                                            <span className="category-label">{category}</span>
                                        </label>
                                    ))}
                                </div>
                            </div> */}
                        </div>
                    )}

                    {/* Step 3: Contact Information */}
                    {currentStep === 3 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Contact Information</h2>
                                <p className="step-subtitle">Where is your store located?</p>
                            </div>
                            
                            <div className="form-section">
                                <div className="input-grid">
                                    <FormInput
                                        label="Store Email"
                                        showLabel
                                        type="email"
                                        value={formData.storeEmail}
                                        onChange={(e) => setFormData({...formData, storeEmail: e.target.value})}
                                        placeholder="store@example.com"
                                    />
                                    
                                    <PhoneInput
                                        label="Store Phone"
                                        showLabel
                                        value={formData.storePhone}
                                        onChange={(e) => setFormData({...formData, storePhone: e.target.value})}
                                        defaultCountryCode="+234"
                                    />

                                    <FormInput
                                        label="Street Address"
                                        showLabel
                                        value={formData.address}
                                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                                        placeholder="Street address"
                                    />

                                    <FormInput
                                        label="City"
                                        showLabel
                                        value={formData.city}
                                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                                        placeholder="City name"
                                    />

                                    <FormInput
                                        label="State"
                                        showLabel
                                        value={formData.state}
                                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                                        placeholder="State name"
                                    />

                                    <FormInput
                                        label="Postal Code"
                                        showLabel
                                        value={formData.postalCode}
                                        onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                                        placeholder="Postal code"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Business Information */}
                    {currentStep === 4 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Business Information</h2>
                                <p className="step-subtitle">Your business and banking details</p>
                            </div>
                            
                            <div className="form-section">
                                <div className="section-title">
                                    <h3>Business Registration</h3>
                                    <span className="section-badge">Required</span>
                                </div>
                                <div className="input-grid">
                                    <FormInput
                                        label="Business Registration Number"
                                        showLabel
                                        value={formData.businessRegistrationNumber}
                                        onChange={(e) => setFormData({...formData, businessRegistrationNumber: e.target.value})}
                                        required
                                        placeholder="e.g., RC-XXXXXXX"
                                    />
                                    
                                    <FormInput
                                        label="Tax ID (TIN)"
                                        showLabel
                                        value={formData.taxId}
                                        onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                                        placeholder="Tax Identification Number"
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <div className="section-title">
                                    <h3>Bank Details</h3>
                                    <span className="section-badge">Required for payments</span>
                                </div>
                                <div className="input-grid">
                                    <FormInput
                                        label="Bank Name"
                                        showLabel
                                        type="select"
                                        placeholder="Select bank..."
                                        value={formData.bankName}
                                        onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                                        required
                                    >
                                        <option value="access">Access Bank</option>
                                        <option value="gtb">GTBank</option>
                                        <option value="first">First Bank</option>
                                        <option value="uba">UBA</option>
                                        <option value="zenith">Zenith Bank</option>
                                        <option value="stanbic">Stanbic IBTC</option>
                                        <option value="fidelity">Fidelity Bank</option>
                                        <option value="union">Union Bank</option>
                                        <option value="wema">Wema Bank</option>
                                        <option value="polaris">Polaris Bank</option>
                                        <option value="keystone">Keystone Bank</option>
                                        <option value="sterling">Sterling Bank</option>
                                        <option value="other">Other</option>
                                    </FormInput>
                                    
                                    <FormInput
                                        label="Account Number"
                                        showLabel
                                        value={formData.accountNumber}
                                        onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                                        required
                                        placeholder="10-digit account number"
                                    />
                                    
                                    <FormInput
                                        label="Account Name"
                                        showLabel
                                        value={formData.accountName}
                                        onChange={(e) => setFormData({...formData, accountName: e.target.value})}
                                        required
                                        placeholder="Account holder name"
                                    />
                                </div>
                                
                                <div className="bank-info-note">
                                    <p>This account will be used for receiving payments from sales on the platform.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Documents */}
                    {currentStep === 5 && (
                        <div className="form-step">
                            <div className="step-header">
                                <h2>Document Verification</h2>
                                <p className="step-subtitle">Upload required documents for verification</p>
                            </div>
                            
                            <div className="form-section">
                                <div className="section-title">
                                    <h3>Required Documents</h3>
                                    <p className="section-hint">Files must be clear and readable. Max 15MB each.</p>
                                </div>
                                
                                <div className="document-uploads">
                                    <FileUpload
                                        label="Business Registration Certificate"
                                        field="businessCertificate"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onUpload={handleFileUpload}
                                        currentFile={formData.businessCertificate}
                                        required={true}
                                        hint="CAC Certificate or Business Registration. PDF, JPG, PNG (max 15MB)"
                                        disabled={isSubmitting}
                                    />
                                    
                                    <FileUpload
                                        label="Tax Certificate (TIN)"
                                        field="taxCertificate"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onUpload={handleFileUpload}
                                        currentFile={formData.taxCertificate}
                                        required={false}
                                        hint="Tax Identification Certificate - Optional. PDF, JPG, PNG (max 15MB)"
                                        disabled={isSubmitting}
                                    />
                                    
                                    <FileUpload
                                        label="Owner ID Document"
                                        field="identityDocument"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onUpload={handleFileUpload}
                                        currentFile={formData.identityDocument}
                                        required={true}
                                        hint="NIN, International Passport, or Driver's License. PDF, JPG, PNG (max 15MB)"
                                        disabled={isSubmitting}
                                    />
                                </div>
                                
                                <div className="document-tips">
                                    <h4>Tips for document upload:</h4>
                                    <ul>
                                        <li>Ensure all text in documents is clearly readable</li>
                                        <li>Take photos in good lighting if scanning is not available</li>
                                        <li>Make sure the entire document is visible in the image</li>
                                        <li>Supported formats: PDF, JPG, PNG (up to 15MB)</li>
                                    </ul>
                                </div>
                                
                                <div className="terms-section" style={{ marginTop: '24px' }}>
                                    <label className="terms-checkbox">
                                        <input type="checkbox" required />
                                        <span className="terms-checkmark"></span>
                                        <span className="terms-text">
                                            I confirm that all information provided is accurate and true. 
                                            I agree to the <a href="/terms">Terms of Service</a>, <a href="/privacy">Privacy Policy</a>, and <a href="/vendor-terms">Vendor Agreement</a>.
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="form-navigation">
                        {currentStep > 1 && currentStep !== 1 && (
                            <Button
                                text="Previous"
                                variant="outline"
                                onClick={() => handleStepNavigation('previous')}
                                disabled={isSubmitting || (currentStep === 2 && isAuthenticated)}
                            />
                        )}
                        
                        {currentStep === 1 ? (
                            // Auth step - navigation handled by EmailFirstAuth
                            null
                        ) : currentStep < totalSteps ? (
                            <Button
                                text="Continue"
                                variant="primary"
                                onClick={() => handleStepNavigation('next')}
                                disabled={isSubmitting}
                            />
                        ) : (
                            <Button
                                text={isSubmitting ? 'Submitting...' : 'Submit Application'}
                                type="submit"
                                variant="primary"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            />
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VendorRegister;